import {useMemo} from "react";
import {DragData, TableOverlay} from "../types";
import {
    FloorplanReservationItemDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {getOverlappingReservations} from "../utils/drop-utils";
import {getFloorplanGuestCount, getGuestCount} from "../utils/reservation-utils";

export function useTableDropState(
    activeDragData: DragData | null,
    overlay: TableOverlay,
    tableReservations: FloorplanReservationItemDTO[],
    visibleReservations: FloorplanReservationItemDTO[],
) {
    const dragType = activeDragData?.type ?? null;
    const dragReservation = activeDragData && (activeDragData.type === "unassigned" || activeDragData.type === "assigned")
        ? activeDragData.reservation
        : undefined;
    const dragReservationId = dragReservation?.reservationId ?? null;
    const dragStart = dragReservation?.restaurantReservation?.expectedStartDate;
    const dragEnd = dragReservation?.restaurantReservation?.expectedEndDate;
    const dragStartMs = dragStart ? new Date(dragStart).getTime() : null;
    const dragEndMs = dragEnd ? new Date(dragEnd).getTime() : null;
    const draggedGuestCount = dragReservation
        ? getFloorplanGuestCount(dragReservation)
        : activeDragData?.type === "halfboard"
            ? getGuestCount(activeDragData.halfboard.accommodationReservation)
            : 0;

    const isDragActive = dragType !== null;

    const dragOverlapping = useMemo(() => {
        if (dragType !== "unassigned" && dragType !== "assigned") return [];
        if (!dragStartMs || !dragEndMs) return [];
        return getOverlappingReservations(
            overlay.id,
            tableReservations,
            new Date(dragStartMs),
            new Date(dragEndMs),
            dragReservationId ?? undefined,
        );
    }, [dragType, dragStartMs, dragEndMs, dragReservationId, overlay.id, tableReservations]);

    const hasTimeConflict = dragOverlapping.length > 0;

    // When dragging, only count guests from reservations that overlap with the
    // dragged reservation's time window — not all visible reservations.
    const capacitySource = isDragActive && dragStartMs && dragEndMs
        ? dragOverlapping
        : visibleReservations;
    const currentGuestCount = capacitySource.reduce(
        (sum, item) => {
            const total = getFloorplanGuestCount(item);
            const tables = item.tableResources ?? [];
            if (tables.length <= 1) return sum + total;
            // Multi-table: only count this table's share
            let remaining = total;
            const idx = tables.findIndex(tr => tr.id === overlay.id);
            for (let i = 0; i < idx && i < tables.length; i++) {
                const cap = tables[i].capacity ?? 4;
                remaining -= Math.min(remaining, cap);
            }
            return sum + Math.min(remaining, overlay.capacity);
        },
        0,
    );

    // Halfboard has no time window yet (period chosen after drop), so only check raw capacity.
    const hasRoom = dragType === "halfboard"
        ? draggedGuestCount <= overlay.capacity
        : currentGuestCount + draggedGuestCount <= overlay.capacity;
    const dragOk = hasRoom && !hasTimeConflict;

    return {isDragActive, dragOk, hasRoom, hasTimeConflict};
}
